/**
 * Copy a JSON-like value with `undefined` object fields removed.
 *
 * Firestore's client SDK rejects `undefined` (`updateDoc` / `setDoc` throw
 * "Unsupported field value: undefined"). Absent and `undefined` mean the
 * same thing in this app, so writers omit the key. Arrays keep their
 * length; only plain-object fields are dropped. Symbols such as the
 * shared-room `DELETE_FIELD` sentinel pass through unchanged.
 */
export function omitUndefinedDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => omitUndefinedDeep(item)) as T;
  }
  if (!isPlainObject(value)) return value;
  const out: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) {
    if (child === undefined) continue;
    out[key] = omitUndefinedDeep(child);
  }
  return out as T;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== "object") return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}
